import { logger } from "@platform/browser-logger";
import {
  compsTableGetCompsTableOptions,
  getResponsesTableOptions,
  useHeyApi,
  type CentroPortalClient,
  type ResponsesTableResponse,
} from "@platform/api";
import { useProposals, type ProposalScope } from "@platform/mpx-quotes-grid";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import {
  cellReviewsQueryKey,
  fetchCellReviews,
} from "../tabs/shared/cellReviews/fetchCellReviews";

import { buildBenefitOrderIndexes } from "./buildBenefitOrderIndex";
import { buildCommentDocument } from "./buildCommentDocument";
import { buildProposalPlanIndex } from "./buildProposalPlanIndex";
import { resolveComments } from "./resolveComments";

import type { ListCellReviewsResponse } from "../tabs/shared/cellReviews/fetchCellReviews";
import type {
  CarrierCommentDocument,
  CoverageRef,
} from "./buildCommentDocument";

const STALE_TIME_MS = 5 * 60 * 1000;
// LOC benefit definitions are static, bundled data — never change mid-session.
const BENEFIT_ORDER_STALE_TIME_MS = Infinity;

interface CellReviewsQueryOptions {
  queryKey: readonly [string, string | undefined];
  queryFn: () => Promise<ListCellReviewsResponse>;
  enabled: boolean;
  staleTime: number;
}

const cellReviewsQueryOptions = (
  scopeType: "proposal_plan" | "response" | "comps",
  ecoRfpId: string | undefined,
  centroPortalClient: CentroPortalClient,
): CellReviewsQueryOptions => ({
  // Shares the Comps/Responses tabs' own cache keys for those two scopes, so
  // this drawer and those grids read one cached list instead of two.
  queryKey: cellReviewsQueryKey(scopeType, ecoRfpId),
  queryFn: () =>
    fetchCellReviews(
      centroPortalClient,
      ecoRfpId
        ? `/rfps/${encodeURIComponent(ecoRfpId)}/cell-reviews?scopeType=${scopeType}`
        : undefined,
      ecoRfpId,
    ),
  enabled: !!ecoRfpId,
  staleTime: STALE_TIME_MS,
});

const toCoverageRefs = (
  coverages: ResponsesTableResponse["coverages"] | undefined,
): CoverageRef[] =>
  (coverages ?? []).map((coverage) => ({
    planGroupId: coverage.planGroupId,
    coverageType: coverage.coverageType,
    label: coverage.label,
    sequence: coverage.sequence,
  }));

export interface UseNeedsReviewCommentsOptions {
  ecoRfpId: string | undefined;
  /** Gates only the Comps row-label fetch — resolved carrier/coverage data
   * and comment counts are always kept current, since the toolbar badge must
   * be accurate before the drawer is ever opened. */
  enabled?: boolean;
}

export interface UseNeedsReviewCommentsResult {
  totalCount: number;
  carriers: CarrierCommentDocument[];
  isLoading: boolean;
  optionLabelByProposalPlanId: ReadonlyMap<string, string>;
}

// The Present tab's Quotes grid gates requote/quote-history data behind a
// flag; the drawer surfaces every non-deleted comment regardless of that
// flag, so it always reads the full board (active + un-actioned requotes).
const PROPOSAL_SCOPE: ProposalScope = "board";

export const useNeedsReviewComments = ({
  ecoRfpId,
  enabled = true,
}: UseNeedsReviewCommentsOptions): UseNeedsReviewCommentsResult => {
  const { centroPortalClient } = useHeyApi();

  const { data: proposals = [], isLoading: isLoadingProposals } = useProposals(
    ecoRfpId ?? "",
    { enabled: !!ecoRfpId },
    PROPOSAL_SCOPE,
  );

  const { data: responsesTableData, isLoading: isLoadingResponsesTable } =
    useQuery({
      ...getResponsesTableOptions({
        path: { id: ecoRfpId ?? "" },
        client: centroPortalClient,
      }),
      enabled: !!ecoRfpId,
      staleTime: STALE_TIME_MS,
    });

  const quotesReviewsQuery = useQuery(
    cellReviewsQueryOptions("proposal_plan", ecoRfpId, centroPortalClient),
  );
  const responsesReviewsQuery = useQuery(
    cellReviewsQueryOptions("response", ecoRfpId, centroPortalClient),
  );
  const compsReviewsQuery = useQuery(
    cellReviewsQueryOptions("comps", ecoRfpId, centroPortalClient),
  );

  const { data: compsTableData } = useQuery({
    ...compsTableGetCompsTableOptions({
      path: { id: ecoRfpId ?? "" },
      client: centroPortalClient,
    }),
    enabled: enabled && !!ecoRfpId,
    staleTime: STALE_TIME_MS,
  });

  const proposalPlanById = useMemo(
    () => buildProposalPlanIndex(proposals),
    [proposals],
  );

  const optionLabelByProposalPlanId = useMemo(
    () =>
      new Map(
        [...proposalPlanById].map(([proposalPlanId, entry]) => [
          proposalPlanId,
          entry.optionLabel,
        ]),
      ),
    [proposalPlanById],
  );

  const { comments, unresolved } = useMemo(
    () =>
      resolveComments({
        quotesReviews: quotesReviewsQuery.data?.reviews ?? [],
        responsesReviews: responsesReviewsQuery.data?.reviews ?? [],
        compsReviews: compsReviewsQuery.data?.reviews ?? [],
        proposalPlanById,
      }),
    [
      quotesReviewsQuery.data,
      responsesReviewsQuery.data,
      compsReviewsQuery.data,
      proposalPlanById,
    ],
  );

  useEffect(() => {
    if (unresolved.length === 0) return;
    logger.warn(
      "Needs Review Comments: some comments did not resolve to a carrier",
      {
        ecoRfpId,
        unresolved,
      },
    );
  }, [ecoRfpId, unresolved]);

  const quotesCoverageTypeKey = useMemo(
    () =>
      [
        ...new Set(
          comments
            .filter((comment) => comment.source === "quotes")
            .map((comment) => comment.coverageType),
        ),
      ]
        .toSorted()
        .join(","),
    [comments],
  );

  const { data: benefitOrderByCoverageType = new Map() } = useQuery({
    queryKey: ["needs-review-comments-benefit-order", quotesCoverageTypeKey],
    queryFn: () =>
      buildBenefitOrderIndexes(
        new Set(quotesCoverageTypeKey.split(",").filter(Boolean)),
      ),
    enabled: quotesCoverageTypeKey.length > 0,
    staleTime: BENEFIT_ORDER_STALE_TIME_MS,
  });

  const responsesCoverages = useMemo(
    () => toCoverageRefs(responsesTableData?.coverages),
    [responsesTableData],
  );

  const carriers = useMemo(
    () => responsesTableData?.carriers ?? [],
    [responsesTableData],
  );

  const comps = useMemo(
    () => ({
      coverageRefs: toCoverageRefs(compsTableData?.coverages),
      subsidyRows: (compsTableData?.subsidyRows ?? []).map((row) => ({
        kind: row.kind,
        label: row.label,
        sequence: row.sequence,
      })),
      notesLabel: compsTableData?.notesRow.label ?? "Notes",
    }),
    [compsTableData],
  );

  const commentDocument = useMemo(
    () =>
      buildCommentDocument({
        comments,
        carriers,
        responsesCoverages,
        comps,
        benefitOrderByCoverageType,
      }),
    [comments, carriers, responsesCoverages, comps, benefitOrderByCoverageType],
  );

  return {
    totalCount: commentDocument.totalCount,
    carriers: commentDocument.carriers,
    isLoading:
      isLoadingProposals ||
      isLoadingResponsesTable ||
      quotesReviewsQuery.isLoading ||
      responsesReviewsQuery.isLoading ||
      compsReviewsQuery.isLoading,
    optionLabelByProposalPlanId,
  };
};
